import { logEvent, Item, Currency, setUserId, setUserProperties } from 'firebase/analytics';
import { Product } from 'common/api/e-comm/models/Product';
import { UniversalBrand } from 'common/api/models/UniversalBrand';
import { firebaseAnalytics } from './firebase';
import { ProductLabel, PurchaseData } from './types';
import { monetize } from 'common/utils/strings';
import { OrderSource } from 'common/api/e-comm/models/OrderSource';
import { CartResponse } from 'common/api/e-comm/cart/lineitem/models/CartItems';
import type { MarketingUTMs } from 'common/features/store/duck/utms/duck';
import { selectMarketingUTMs } from 'common/features/store/duck/utms/selectors';
import { FeaturesState } from 'common/features/featuresReducer';
import { NavOptions } from 'components/search/components/ShopNavOptions';
import { clearInviteHubTool, getInviteHubTool } from 'utils/inviteHubTool';
import { posthog } from './posthog';
import enrollmentStepToPosthog, { enrollmentStepNameToPosthog } from './posthogEventHelper';

let globalStore: { getState: () => FeaturesState } | null = null;

export const setAnalyticsGlobalStore = (store: { getState: () => FeaturesState }) => {
    globalStore = store;
};

interface RawItem {
    product: Product;
    brand?: UniversalBrand | null;
    price?: Currency;
    quantity: number;
}

interface GAItem extends Item {
    price?: Currency;
}

interface PosthogEnrollmentAnalyticsOptions {
    orgId?: string | null;
    programType?: string | null;
    flowVariant?: string | null;
    experimentName?: string;
    experimentVariant?: 'control' | 'variant';
    hasExistingAccount?: boolean;
    verificationAttempts?: number;
    completionTimeSeconds?: number;
}

export const GA_WEB_ENROLLMENT_EXPERIMENT_NAME = 'web_enrollment_test';

export enum PromotionSources {
    Contentful = 'Contentful',
    TodaysBonus = "Today's Bonus",
    RaiseRight = 'RaiseRight',
}

export enum EarnMoreDestinationType {
    TravelAndEarn = 'travel and earn',
    FlightGiftCards = 'flight gift cards',
    CruiseGiftCards = 'cruise gift cards',
    BookTravel = 'book travel',
    LocalDining = 'local dining',
    InviteFriendAndFamily = 'invite friend and family',
    BrowserExtension = 'browser extension',
    TopDeals = 'top deals',
    AllBonuses = 'all bonuses',
}

export enum TravelDestinationType {
    BookHotel = 'book hotel',
    BookCar = 'book car',
    BookAttraction = 'book attraction',
    Flights = 'flights',
    Cruises = 'cruises',
    Lodging = 'lodging',
    CampingAndRV = 'camping and rv',
    Transportation = 'transportation',
    Gas = 'gas',
    LocalDining = 'local dining',
}

export interface PromotionData {
    creativeSlot: string;
    index: number;
    promotionName: string;
    source: PromotionSources;
    carouselPosition?: number;
}

interface AffiliatePurchaseData {
    affiliateChannel: string;
    affiliateMobilePlatform: 'ios' | 'android' | '';
    version: string;
    location: 'wallet' | 'order summary' | 'PDP';
    brandId: number | null;
    brandName: string;
    orgId: string | null;
}

const CURRENCY = 'USD';

const rawItemToGAItem = ({ product, brand, price, quantity }: RawItem): GAItem => {
    const itemPrefix = product.isLocalProduct ? product.name : brand?.name;
    const itemName = `${itemPrefix} ${ProductLabel[product.type]}`;
    return {
        item_brand: brand?.name,
        item_id: String(product.id),
        item_name: itemName,
        item_category: ProductLabel[product.type],
        item_variant: price ? monetize(Number(price)) : undefined,
        quantity,
        price,
    };
};

const getItemValue = (rawItem: RawItem): number | undefined => {
    if (rawItem.price) {
        return Number(rawItem.price) * rawItem.quantity;
    }
};

export const gaLogClickTopBanner = (type: 'travel' | 'browser_extension', text: string) => {
    logEvent(firebaseAnalytics, 'clicked_top_nav_banner', {
        banner_copy: text,
        banner_id: type,
    });
};

export const gaLogAddToCart = (rawItem: RawItem, source: 'PDP' | 'wallet' = 'PDP') => {
    const value = getItemValue(rawItem) || 0;
    const gaItem = rawItemToGAItem(rawItem);
    logEvent(firebaseAnalytics, 'add_to_cart', {
        items: [gaItem],
        currency: CURRENCY,
        value,
    });

    if (posthog?.capture) {
        posthog.capture('checkout:add_to_cart_clicked', {
            product_id: rawItem.product.id,
            brand_id: rawItem.brand?.id,
            brand_name: rawItem.brand?.name,
            quantity: rawItem.quantity,
            unit_price: rawItem.price,
            subtotal: value,
            is_buy_now: false,
            source,
        });
    }
};

export const gaLogClickBuyNow = (
    productId: number,
    price: number,
    qt: number,
    options?: { brandId?: number; brandName?: string; orgId?: string | null }
) => {
    logEvent(firebaseAnalytics, 'click_buy_now', {
        params: {
            product: productId,
            value: price * qt,
            qty: qt,
        },
    });

    if (posthog?.capture) {
        const subtotal = price * qt;
        posthog.capture('checkout:buy_now_clicked', {
            product_id: productId,
            brand_id: options?.brandId,
            brand_name: options?.brandName,
            quantity: qt,
            unit_price: price,
            subtotal,
            is_buy_now: true,
            source: 'PDP',
            org_id: options?.orgId,
        });
    }
};

export const gaSetUserId = (userId: string | null) => {
    if (userId) {
        setUserId(firebaseAnalytics, userId);
        if (posthog?.identify) {
            posthog.identify(userId);
        }
    }
};

export interface PurchaseDataWithUTMs extends PurchaseData {
    utmSource?: string;
    utmMedium?: string;
    utmCampaign?: string;
    utmChannel?: string;
}

export const gaLogPurchase = (
    purchaseData: PurchaseData | PurchaseDataWithUTMs,
    orgId: string | null,
    paymentMethodType: string | null
): void => {
    const {
        cart,
        total,
        shippingCost,
        orderId,
        isBuyNow,
        utmSource,
        utmMedium,
        utmCampaign,
        utmChannel,
    } = purchaseData as PurchaseDataWithUTMs;

    let marketingUTMs: MarketingUTMs = {};
    try {
        marketingUTMs = utmSource
            ? {
                  utm_source: utmSource,
                  utm_medium: utmMedium,
                  utm_campaign: utmCampaign,
                  utm_channel: utmChannel,
              }
            : globalStore
            ? selectMarketingUTMs(globalStore.getState()) || {}
            : {};
    } catch (error) {
        marketingUTMs = {};
    }

    const eventParams: Record<string, any> = {
        affiliation: OrderSource.Web,
        currency: CURRENCY,
        items: cart.lineItems.map(rawItemToGAItem),
        shipping: shippingCost,
        tax: 0,
        value: total,
        transaction_id: orderId,
        isBuyNow,
    };

    if (marketingUTMs.utm_source) {
        eventParams.utm_source = marketingUTMs.utm_source;
    }
    if (marketingUTMs.utm_medium) {
        eventParams.utm_medium = marketingUTMs.utm_medium;
    }
    if (marketingUTMs.utm_campaign) {
        eventParams.utm_campaign = marketingUTMs.utm_campaign;
    }
    if (marketingUTMs.utm_channel) {
        eventParams.utm_channel = marketingUTMs.utm_channel;
    }

    logEvent(firebaseAnalytics, 'purchase' as any, eventParams);

    if (posthog?.capture) {
        posthog.capture('checkout:purchase_completed', {
            order_id: orderId,
            order_total: total,
            shipping_cost: shippingCost,
            tax: 0,
            is_buy_now: isBuyNow,
            payment_method_type: paymentMethodType,
            org_id: orgId,
            utm_source: marketingUTMs.utm_source,
            utm_medium: marketingUTMs.utm_medium,
            utm_campaign: marketingUTMs.utm_campaign,
            utm_channel: marketingUTMs.utm_channel,
        });
    }
};

export const gaLogBeginCheckout = (cart: CartResponse) => {
    logEvent(firebaseAnalytics, 'begin_checkout', {
        items: cart.lineItems.map(rawItemToGAItem),
        currency: CURRENCY,
        value: cart.subtotal,
    });

    if (posthog?.capture) {
        posthog.capture('checkout:cart_viewed', {
            item_count: cart.lineItems?.length ?? 0,
            cart_subtotal: cart.subtotal,
        });
    }
};

// this should not be called under normal usage.
export const logPageView = ({
    pagePath,
    pageLocation,
}: {
    pagePath: string;
    pageLocation: string;
}) => {
    logEvent(firebaseAnalytics, 'page_view', {
        page_path: pagePath,
        page_location: pageLocation,
    });
};

export const logClickAd = ({
    promotionName,
    index,
    source,
    creativeSlot,
    carouselPosition,
}: PromotionData) => {
    logEvent(firebaseAnalytics, 'click_ad', {
        creative_name: promotionName,
        creative_slot: creativeSlot || `${source} (${index + 1})`,
        ...(carouselPosition !== undefined && { carousel_position: carouselPosition }),
    });
};

export const logViewAd = ({
    promotionName,
    index,
    source,
    creativeSlot,
    carouselPosition,
}: PromotionData) => {
    logEvent(firebaseAnalytics, 'view_ad', {
        creative_name: promotionName,
        creative_slot: creativeSlot || `${source} (${index + 1})`,
        ...(carouselPosition !== undefined && { carousel_position: carouselPosition }),
    });
};

export const gaSetUserProperties = (properties: { [key: string]: unknown }) => {
    setUserProperties(firebaseAnalytics, properties);
};

export const logGetCoordinatorInvite = (type: 'code' | 'qr' | 'text' | 'email') => {
    logEvent(firebaseAnalytics, `click_get_coordinator_invite_${type}`);
};

export const logAffiliateClick = ({
    affiliateChannel,
    affiliateMobilePlatform,
    version,
    location,
    brandId,
    brandName,
    orgId,
}: AffiliatePurchaseData) => {
    let marketingUTMs: MarketingUTMs = {};
    try {
        marketingUTMs = globalStore ? selectMarketingUTMs(globalStore.getState()) || {} : {};
    } catch (error) {
        marketingUTMs = {};
    }

    const eventParams: Record<string, any> = {
        affiliate_channel: affiliateChannel,
        affiliate_mobile_platform: affiliateMobilePlatform,
        version,
        location,
        brand_id: brandId,
        brand_name: brandName,
        org_id: orgId,
    };

    const hasMarketingUTMs = Object.keys(marketingUTMs).length > 0;
    if (hasMarketingUTMs) {
        eventParams.marketingUTMs = marketingUTMs;
    }

    logEvent(firebaseAnalytics, 'affiliate_click', eventParams);
};

export const logP2PInviteFamily = (type: 'email' | 'text' | 'copy' | 'share', source: string) => {
    logEvent(firebaseAnalytics, `click_invite_family_${type}_icon`, { source });
};

export const logCardEnrolledLocalDining = (
    linkSuccessful: boolean,
    cardEnrollmentLocation: 'my_cards' | 'link_a_card' | 'payment_options' | 'checkout'
) => {
    logEvent(firebaseAnalytics, 'card_enrolled_local_dining', {
        link_successful: linkSuccessful,
        card_enrollment_location: cardEnrollmentLocation,
    });
};

export const logViewedCheckout = (
    action: null | 'clicked_link_card',
    userId: string,
    options?: { availablePaymentMethods?: string[]; selectedDefaultMethod?: string }
) => {
    logEvent(firebaseAnalytics, 'viewed_checkout', {
        checkout_action: action,
        userId,
    });

    if (posthog?.capture) {
        posthog.capture('checkout:payment_options_viewed', {
            available_payment_methods: options?.availablePaymentMethods,
            selected_default_method: options?.selectedDefaultMethod,
        });
    }
};

export const logOrderReviewViewed = () => {
    if (posthog?.capture) {
        posthog.capture('checkout:order_review_viewed');
    }
};

// Fires once when the org lifetime earnings info tooltip opens; never on close.
// PostHog only, no GA event for this card.
export const logOrganizationEarningsTooltipOpened = (organizationId: string) => {
    if (posthog?.capture) {
        posthog.capture('organization_earnings_tooltip_opened', {
            organization_id: organizationId,
            platform: 'web',
        });
    }
};

export type WalletAdLocation = 'wallet_view' | 'gift_card_details';

export const logFGDealClick = ({
    affiliateMobilePlatform,
    brandId,
    brandName,
    dealType,
    location,
    orgId,
    version,
}: {
    affiliateMobilePlatform?: 'web' | 'mobile-web';
    brandId: string;
    brandName: string;
    dealType: 'deal' | 'coupon';
    location: 'PDP' | 'homepage' | 'homepage_category_top_deals' | 'wallet_ad';
    orgId?: string | null;
    version: string;
}) => {
    logEvent(firebaseAnalytics, 'affiliate_click', {
        affiliate_channel: 'web',
        affiliate_mobile_platform: affiliateMobilePlatform || 'web',
        brand_id: brandId,
        brand_name: brandName,
        deal_type: dealType,
        location,
        org_id: orgId,
        version,
    });
};

export const logClickWalletAd = ({
    location,
    brandId,
    brandName,
    orgId,
}: {
    location: WalletAdLocation;
    brandId: string;
    brandName: string;
    orgId?: string | null;
}) => {
    logEvent(firebaseAnalytics, 'click_wallet_ad', {
        location,
        brandid: brandId,
        brand_name: brandName,
        org_id: orgId,
    });
};

export const logViewWalletAd = ({
    location,
    brandId,
    brandName,
    orgId,
}: {
    location: WalletAdLocation;
    brandId: string;
    brandName: string;
    orgId?: string | null;
}) => {
    logEvent(firebaseAnalytics, 'view_wallet_ad', {
        location,
        brandid: brandId,
        brand_name: brandName,
        org_id: orgId,
    });
};

export const logEarnMoreShopNavClick = ({ orgId }: { orgId?: string | null }) => {
    logEvent(firebaseAnalytics, 'click_earn_more', {
        org_id: orgId,
    });
};

export const logMainShopNavClick = ({ tab }: { tab: NavOptions }) => {
    logEvent(firebaseAnalytics, 'click_navigation', {
        destination: tab,
    });
};

export const logEarnMoreViewPage = ({ orgId }: { orgId?: string | null }) => {
    logEvent(firebaseAnalytics, 'view_earn_more', {
        affiliate_channel: 'web',
        org_id: orgId,
    });
};

export const logTravelEarnViewPage = ({ orgId }: { orgId?: string | null }) => {
    logEvent(firebaseAnalytics, 'view_travel_and_earn', {
        affiliate_channel: 'web',
        org_id: orgId,
    });
};

export const logEarnMoreClickCTA = ({
    destinationType,
    orgId,
}: {
    destinationType: EarnMoreDestinationType;
    orgId?: string | null;
}) => {
    logEvent(firebaseAnalytics, 'click_earn_more_cta', {
        affiliate_channel: 'web',
        destination_type: destinationType,
        org_id: orgId,
    });
};

export const logTravelAndEarnClickCTA = ({
    destinationType,
    orgId,
}: {
    destinationType: TravelDestinationType;
    orgId?: string | null;
}) => {
    logEvent(firebaseAnalytics, 'click_travel_and_earn_cta', {
        affiliate_channel: 'web',
        destination_type: destinationType,
        org_id: orgId,
    });
};

export const logViewGiftCardPDP = ({
    productId,
    brandId,
    brandName,
    orgId,
}: {
    productId?: string | number | null;
    brandId?: string | null;
    brandName?: string | null;
    orgId?: string | null;
}) => {
    logEvent(firebaseAnalytics, 'view_gift_card_pdp', {
        brand_id: brandId,
        brand_name: brandName,
        org_id: orgId,
    });

    if (posthog?.capture) {
        posthog.capture('checkout:gift_card_product_viewed', {
            product_id: productId ?? brandId,
            brand_id: brandId,
            brand_name: brandName,
            org_id: orgId,
        });
    }
};

export const logParticipantEnrollment = (
    stepNumber: number,
    stepName: string,
    options: PosthogEnrollmentAnalyticsOptions = {},
    experimentData?: { experiment_name: string; experiment_variant: 'control' | 'variant' }
) => {
    const resolvedExperimentData =
        options.experimentName && options.experimentVariant
            ? {
                  experiment_name: options.experimentName,
                  experiment_variant: options.experimentVariant,
              }
            : experimentData;

    const shouldLogGa = stepNumber !== 0 || Boolean(resolvedExperimentData);
    if (shouldLogGa) {
        logEvent(firebaseAnalytics, 'participant_enrollment', {
            participant_enrollment_step: stepNumber,
            participant_enrollment_step_name: stepName,
            ...(resolvedExperimentData && resolvedExperimentData),
        });
    }

    const mapping = enrollmentStepNameToPosthog(stepName) || enrollmentStepToPosthog(stepNumber);
    if (mapping && posthog?.capture) {
        posthog.capture(mapping.event, {
            step_key: mapping.step_key,
            step_number: stepNumber,
            step_name: stepName,
            flow_variant: options.flowVariant,
            org_id: options.orgId,
            program_type: options.programType,
            experiment_name: resolvedExperimentData?.experiment_name,
            experiment_variant: resolvedExperimentData?.experiment_variant,
            has_existing_account: options.hasExistingAccount,
            verification_attempts: options.verificationAttempts,
            completion_time_seconds: options.completionTimeSeconds,
            // Invite Hub attribution (PT-4138) — null outside Invite Hub-sourced sessions.
            invite_hub_tool: getInviteHubTool(),
        });
        // Attribution ends with the funnel; a later enrollment in this tab must not
        // inherit this one's tool unless it arrives through a fresh Invite Hub link.
        if (mapping.step_key === 'enrollment_completed') {
            clearInviteHubTool();
        }
    }
};

export const logEnrollmentAccountSetupCompleted = (userId?: string) => {
    if (!posthog?.capture) {
        return;
    }

    posthog.capture('enrollment:account_setup_completed', {
        step_key: 'account_setup_completed',
        step_number: 8,
        ...(userId ? { user_id: userId } : {}),
    });
};

export const logAccountCreationMethodSelected = (
    signupMethod: 'google' | 'apple' | 'email',
    stepNumber: number
) => {
    if (!posthog?.capture) {
        return;
    }
    posthog.capture('enrollment:account_creation_method_selected', {
        signup_method: signupMethod,
        step_key: 'account_creation_method',
        step_number: stepNumber,
    });
};

/** PostHog funnel events for org (coordinator) enrollment v2 — `/enroll/start-program/v2/*` */
export const logOrgEnrollmentV2 = (
    event: string,
    properties?: Record<string, string | number | boolean | null | undefined>
) => {
    if (!posthog?.capture) {
        return;
    }
    posthog.capture(event, {
        enrollment_flow: 'org',
        enrollment_version: 'v2',
        ...properties,
    });
};

export const logStartAProgramSelected = (experimentVariant: 'control' | 'variant') => {
    logEvent(firebaseAnalytics, 'start_a_program_selected');

    if (posthog?.capture) {
        posthog.capture('enrollment:start_a_program_selected', {
            step_name: 'start_a_program_selected',
            step_number: 2,
            experiment_name: GA_WEB_ENROLLMENT_EXPERIMENT_NAME,
            experiment_variant: experimentVariant,
        });
    }
};

export const logSwiperSlideView = (swiper: string, slide: string, carouselPosition?: number) => {
    logEvent(firebaseAnalytics, 'swiper_slide_view', {
        swiper,
        slide,
        ...(carouselPosition !== undefined && { carousel_position: carouselPosition }),
    });
};
